<script setup>
import { ref, watch, onMounted, getCurrentInstance } from 'vue';
import { usePlaylistStore } from "@/stores/playlist.js";
import { enableScroll, disableScroll, scrollToSlide } from '@/Helpers/scroll.js'
import NudgeRow from '@/Pages/Playlist/components/Misc/NudgeRow.vue'
import { resolveColourValue } from '@/Helpers/themeColourResolver';

const plStore = usePlaylistStore();
const instance = getCurrentInstance();
const props = defineProps({
    row: {
        type: Object,
        default: undefined,
    },
    cardIndex: {
        type: Number,
        default: undefined,
    },
    playlistId: {
        type: Number,
        default: undefined,
    },
    isIntersecting: Boolean,
    scormExport: Boolean,
})
const showNudgeTitle = ref(true)
const nudges = ref([])
const isAnswered = ref(false);
const choices = ref([]);

const scormApi = ref(null);
if (props.scormExport) {
    scormApi.value = instance.appContext.config.globalProperties.$scorm.api;
}

onMounted(() => {
    const isDisabled = (plStore.previousNudges.length > 0)
    props.row.content.nudges.forEach(nudge => {
        let isEnabled = false;
        if (plStore.previousNudges.length > 0) {
            isEnabled = plStore.previousNudges.filter((item) => item.title === nudge.content).length > 0;
            isAnswered.value = true;
        }
        nudges.value.push({
            label: nudge.content,
            description: nudge.description,
            disabled: isDisabled,
            enabled: isEnabled
        })

        if (props.scormExport) {
            choices.value = nudges.value.map(nudge => nudge.label);
        }
    })
});

watch(
    () => plStore.fontScale,
    (newVal, prevVal) => {
        if (newVal < 0.60) { showNudgeTitle.value = false } else { showNudgeTitle.value = true }
    }
)

watch(
    () => props.isIntersecting,
    (newVal, oldVal) => { 
        if (props.isIntersecting === true && isAnswered.value === false) {
            disableScroll();
        } else if (props.isIntersecting === true && isAnswered.value === true) {
            enableScroll();
        }
        if (newVal === false && oldVal === true) {
            enableScroll();
        }
    }
);

function storeNudges() {
    if (isAnswered.value) {
        scrollToSlide(props.cardIndex + 1);
        return false
    }
    const data = {
        nudges: nudges.value.filter(nudge => nudge.enabled).map(nudge => nudge.label),
        playlist_id: props.playlistId,
        playlist_card_row_id: props.row.id,
        isCorrect: true,
    }
    
    if (props.scormExport && scormApi.value) {
        data.choices = choices.value;
        data.correct_responses = props.row.content.nudges.map((nudge, index) => index);
        data.type = 'Nudge';
        scormApi.value.recordInteraction(data);
        scrollToSlide(props.cardIndex + 1);
        isAnswered.value = true;
    } else {
        const url = '/activity/store_nudges'
        if (data.nudges.length == 0) {
            isAnswered.value = true;
            scrollToSlide(props.cardIndex + 1);
            return false;
        }
        axios.post(url, data)
            .then(function () {
                scrollToSlide(props.cardIndex + 1);
                isAnswered.value = true;
            })
            .catch(function (error) {
                console.error(error)
                scrollToSlide(props.cardIndex + 1);
            });
    }
    
}
</script>
<template>
    <div class="relative p-fl-4 h-full w-full flex flex-col">
        <div
            class="font-bold text-fl-5xl w-full py-fl-4"
            :class="[{ 'hidden': !showNudgeTitle }]"
            v-text="row.content.title"
        />
        <div class="font-bold text-fl-lg w-full py-fl-4" v-text="row.content.subtitle" />
        <ul class="flex flex-col gap-y-2 px-fl-4">
            <NudgeRow
                v-for="(nudge, index) in nudges"
                :key="index"
                v-model="nudge.enabled"
                :toggle-label="nudge.label"
                :toggle-description="''"
                :disabled="nudge.disabled"
                :text-colour="resolveColourValue(plStore.cards[props.cardIndex].theme?.content.card_text, plStore.brand)"
                :toggle-active-colour="resolveColourValue(plStore.cards[props.cardIndex].theme?.content.button_bg, plStore.brand)"
                :toggle-circle-colour="resolveColourValue(plStore.cards[props.cardIndex].theme?.content.button_text, plStore.brand)"
            />
        </ul>
        <div class="grow font-bold text-fl-lg w-full py-fl-4" v-text="row.content.outro" />
        <div class="flex flex-col w-full">
            <button
                type="button"
                class="place-self-end mr-4 rounded-full
            px-fl-4
            py-fl-2
            font-bold
            shadow-sm 
            border-2
            focus-visible:outline 
            focus-visible:outline-2 
            focus-visible:outline-offset-2 
            focus-visible:outline-white"
                @click="storeNudges"
            >
                {{ isAnswered ? 'Continue' : 'Set Nudges' }}
            </button>
        </div>
    </div>
</template>

<style scoped>
button {
    border-color: var(--button_bg);
    background-color: var(--button_bg);
    color: var(--button_text);
}

button:hover {
    background-color: var(--button_hover_bg);
    color: var(--button_hover_text);
}
</style>